import React, { useState, useMemo } from 'react';
import { ArrowLeft, Heart, Share2, Star, Crown, MapPin, Clock, Phone, Globe, ChevronRight, Calendar, ShoppingBag, CheckCircle, X, ChevronLeft, ChevronDown, Building2, ExternalLink, Ticket, BookOpen, Key, CalendarDays, Users, Car, Ship, Hotel } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { OFFICES, SERVICES, PRODUCTS, COURSES, RENTALS, EVENTS, COMPANIES, getOfficesByCompanyId, getReviewsByOfficeId, getUserById } from '../data/database';
import { ServiceCard, ProductCard, CourseCard, EventCard } from '../components/ItemCards';

const OfficePage = ({ officeId, onBack, onNavigate }) => {
  const { addToCart, cart, toggleFavorite, isFavorite: checkFavorite } = useApp();
  const [activeTab, setActiveTab] = useState('services');
  const [selectedService, setSelectedService] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const isFavorite = checkFavorite(officeId, 'offices');
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedDate, setSelectedDate] = useState(0);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showGallery, setShowGallery] = useState(false);

  // Office data - this page represents a SINGLE office
  // Support both numeric ID and string slug
  const office = OFFICES.find(o => 
    o.id === officeId || 
    o.id === parseInt(officeId) || 
    o.slug === officeId
  ) || OFFICES[0];
  const company = COMPANIES.find(c => c.id === office.companyId);
  const companyOffices = getOfficesByCompanyId(office.companyId);
  const otherOffices = companyOffices.filter(o => o.id !== office.id);
  
  // Data filtered by THIS office
  const services = SERVICES.filter(s => s.officeId === office.id);
  const rentals = (RENTALS || []).filter(r => r.officeId === office.id);
  const products = PRODUCTS.filter(p => p.officeId === office.id);
  const events = (EVENTS || []).filter(e => e.officeId === office.id);
  const courses = (COURSES || []).filter(c => c.officeId === office.id);
  const officeCartItems = cart.filter(c => c.officeId === office.id);
  const cartTotal = officeCartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);

  // Determine available tabs based on content
  const availableTabs = useMemo(() => {
    const tabs = [];
    if (services.length > 0) tabs.push({ id: 'services', label: 'Appuntamenti', icon: '🕐', badge: 'orario' });
    if (rentals.length > 0) tabs.push({ id: 'rentals', label: 'Noleggi', icon: '🔑', badge: 'giornaliero' });
    if (products.length > 0) tabs.push({ id: 'products', label: 'Prodotti', icon: '🛍️' });
    if (events.length > 0) tabs.push({ id: 'events', label: 'Eventi', icon: '🎟️' });
    if (courses.length > 0) tabs.push({ id: 'courses', label: 'Corsi', icon: '📚' });
    tabs.push({ id: 'reviews', label: 'Recensioni', icon: '⭐' });
    tabs.push({ id: 'info', label: 'Info', icon: 'ℹ️' });
    return tabs;
  }, [services.length, rentals.length, products.length, events.length, courses.length]);

  // Gallery images: usa le immagini dell'office o un placeholder
  const galleryImages = useMemo(() => {
    if (office.images?.length > 0) return office.images;
    return [office.image || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800'];
  }, [office.images, office.image]);

  // Genera date dinamicamente a partire da oggi
  const dates = useMemo(() => {
    const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    const result = [];
    const today = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      if (i === 0) result.push('Oggi');
      else if (i === 1) result.push('Domani');
      else result.push(`${dayNames[d.getDay()]} ${d.getDate()}`);
    }
    return result;
  }, []);

  // Genera orari dinamicamente dagli orari dell'office
  const times = useMemo(() => {
    const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const today = new Date();
    const dayKey = dayNames[today.getDay()];
    const officeHours = office.hours?.[dayKey];
    
    if (!officeHours) return ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
    
    const openHour = parseInt(officeHours.open?.split(':')[0]) || 9;
    const closeHour = parseInt(officeHours.close?.split(':')[0]) || 18;
    const slots = [];
    for (let h = openHour; h < closeHour; h++) {
      slots.push(`${h.toString().padStart(2, '0')}:00`);
    }
    return slots.length > 0 ? slots : ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
  }, [office.hours]);

  // Recensioni dal database
  const reviews = useMemo(() => getReviewsByOfficeId(office.id), [office.id]);

  const handleBook = (service) => {
    setSelectedService(service);
    setShowBooking(true);
  };

  const confirmBooking = () => {
    setShowBooking(false);
    setSelectedService(null);
    alert('Prenotazione confermata!');
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Image Slider/Gallery */}
      <div className="relative h-64 sm:h-80 lg:h-96">
        <img 
          src={galleryImages[currentImage]} 
          alt={office.name} 
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => setShowGallery(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
        
        {/* Slider Navigation Arrows */}
        {galleryImages.length > 1 && (
          <>
            <button 
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
        
        {/* Header Actions */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between">
          <button 
            onClick={onBack}
            className="w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => toggleFavorite(officeId, 'offices')}
              className="w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              data-testid="business-favorite-btn"
            >
              <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-700'}`} />
            </button>
            <button className="w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors">
              <Share2 className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Image Counter & Gallery Button */}
        <button 
          onClick={() => setShowGallery(true)}
          className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/50 backdrop-blur-sm text-white text-sm px-3 py-2 rounded-full hover:bg-black/70 transition-colors"
        >
          <span>{currentImage + 1}/{galleryImages.length}</span>
          <span className="text-white/70">• Vedi tutte</span>
        </button>

        {/* Image Dots */}
        {galleryImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {galleryImages.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentImage(i)}
                className={`w-2 h-2 rounded-full transition-all ${currentImage === i ? 'bg-white w-4' : 'bg-white/50'}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Business Info Card */}
      <div className="bg-white rounded-t-3xl -mt-6 relative z-10">
        <div className="max-w-screen-2xl mx-auto p-5">
          {/* Logo Avatar + Business Name */}
          <div className="flex items-start gap-4">
            {/* Avatar/Logo */}
            <div className="relative flex-shrink-0">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-4 border-white shadow-lg overflow-hidden bg-white -mt-12">
                <img 
                  src={office.logo || office.image || galleryImages[0]} 
                  alt={`${office.name} logo`}
                  className="w-full h-full object-cover"
                />
              </div>
              {office.isElit && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow">
                  <Crown className="w-3.5 h-3.5 text-white" />
                </div>
              )}
            </div>

            {/* Name & Category */}
            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{office.name}</h1>
                {office.isElit && (
                  <span className="flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex-shrink-0">
                    <Crown className="w-3 h-3" />
                    Elit
                  </span>
                )}
              </div>
              <p className="text-gray-500">{office.category}</p>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-1 bg-emerald-50 px-3 py-1.5 rounded-xl flex-shrink-0">
              <Star className="w-5 h-5 text-emerald-500 fill-emerald-500" />
              <span className="font-bold text-emerald-600">{office.rating}</span>
            </div>
          </div>

          <p className="text-gray-600 mt-3">{office.description}</p>

          {/* Office Address */}
          <div className="mt-4 flex items-center gap-3 bg-gray-50 rounded-xl p-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-left flex-1">
              <p className="font-medium text-gray-900">{office.address}</p>
              {office.distance && <p className="text-sm text-gray-500">{office.distance}</p>}
            </div>
          </div>

          {/* Other Offices of Same Company */}
          {otherOffices.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-2 px-1">Altre sedi di {company?.name}</p>
              {otherOffices.map(o => (
                <button
                  key={o.id}
                  onClick={() => onNavigate('office', { id: o.id })}
                  className="w-full flex items-center gap-3 bg-gray-50 hover:bg-emerald-50 rounded-xl p-3 mb-1.5 transition-colors text-left"
                  data-testid={`other-office-${o.id}`}
                >
                  <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{o.name}</p>
                    <p className="text-xs text-gray-500 truncate">{o.address}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-4">
            <a href={`tel:${office.phone}`} className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 text-white py-3 rounded-xl font-medium hover:bg-emerald-600 transition-colors">
              <Phone className="w-5 h-5" />
              Chiama
            </a>
            <button className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors">
              <MapPin className="w-5 h-5" />
              Mappa
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-100 sticky top-0 bg-white z-20">
          <div className="max-w-screen-2xl mx-auto flex overflow-x-auto scrollbar-hide">
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[80px] py-4 text-sm font-medium transition-all relative whitespace-nowrap ${
                  activeTab === tab.id ? 'text-emerald-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-emerald-500 rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-screen-2xl mx-auto p-5 lg:p-6">
          {activeTab === 'services' && services.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-emerald-500" />
                <h3 className="font-semibold text-gray-900 lg:text-lg">Servizi ad Appuntamento</h3>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">prenotazioni orarie</span>
              </div>
              {office.isElit && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-amber-500" />
                    <span className="font-semibold text-amber-700">Offerte Esclusive Elit</span>
                  </div>
                  <p className="text-sm text-amber-600">Servizi speciali disponibili solo per questo partner Elit</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                {services.map((service) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    onBook={() => handleBook(service)}
                    onClick={() => onNavigate('service-detail', { id: service.id })}
                  />
                ))}
              </div>
            </div>
          )}

          {activeTab === 'rentals' && rentals.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Key className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold text-gray-900 lg:text-lg">Noleggi e Soggiorni</h3>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">prenotazioni giornaliere</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                {rentals.map((rental) => {
                  const price = rental.pricePerNight || rental.pricePerDay;
                  const priceLabel = rental.pricePerNight ? '/notte' : '/giorno';
                  const isHotel = rental.pricePerNight;
                  const isCar = ['economy', 'premium', 'suv', 'electric'].includes(rental.category);
                  const isBoat = ['gozzo', 'yacht', 'gommone', 'catamarano'].includes(rental.category);
                  
                  return (
                    <div 
                      key={rental.id}
                      onClick={() => onNavigate('rental-detail', { id: rental.id })}
                      className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-blue-200 hover:shadow-lg transition-all cursor-pointer group"
                      data-testid={`rental-card-${rental.id}`}
                    >
                      <div className="relative h-40">
                        <img 
                          src={rental.images?.[0] || 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800'} 
                          alt={rental.name} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                        <div className="absolute top-2 left-2">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1 ${
                            isHotel ? 'bg-blue-500 text-white' : 
                            isCar ? 'bg-purple-500 text-white' : 
                            isBoat ? 'bg-cyan-500 text-white' : 
                            'bg-emerald-500 text-white'
                          }`}>
                            {isHotel ? <Hotel className="w-3 h-3" /> : 
                             isCar ? <Car className="w-3 h-3" /> : 
                             isBoat ? <Ship className="w-3 h-3" /> : 
                             <Key className="w-3 h-3" />}
                            {rental.category}
                          </span>
                        </div>
                        {(rental.maxGuests || rental.maxPassengers) && (
                          <div className="absolute bottom-2 left-2">
                            <span className="text-xs bg-black/60 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              Max {rental.maxGuests || rental.maxPassengers}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <h4 className="font-semibold text-gray-900 truncate">{rental.name}</h4>
                        {rental.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{rental.description}</p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <div>
                            <span className="font-bold text-blue-600">€{price}</span>
                            <span className="text-gray-500 text-sm">{priceLabel}</span>
                          </div>
                          {(rental.minStay || rental.minDays) > 1 && (
                            <span className="text-xs text-gray-400">
                              Min {rental.minStay || rental.minDays} {rental.minStay ? 'notti' : 'giorni'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'products' && products.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-4">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={{...product, officeId: office.id}}
                  onClick={() => onNavigate('product-detail', { id: product.id })}
                />
              ))}
            </div>
          )}

          {activeTab === 'events' && events.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Ticket className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold text-gray-900 lg:text-lg">Eventi in programma</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                {events.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onClick={() => onNavigate('event-detail', { id: event.id })}
                  />
                ))}
              </div>
            </div>
          )}

          {activeTab === 'courses' && courses.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold text-gray-900 lg:text-lg">Corsi disponibili</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                {courses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    onClick={() => onNavigate('course-detail', { id: course.id })}
                  />
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {reviews.length > 0 ? reviews.map((review) => {
                const reviewDate = new Date(review.date);
                const now = new Date();
                const diffDays = Math.floor((now - reviewDate) / (1000 * 60 * 60 * 24));
                const dateLabel = diffDays === 0 ? 'Oggi' : diffDays === 1 ? 'Ieri' : diffDays < 7 ? `${diffDays} giorni fa` : diffDays < 30 ? `${Math.floor(diffDays / 7)} settimana/e fa` : `${Math.floor(diffDays / 30)} mese/i fa`;
                
                return (
                  <div key={review.id} className="bg-white border border-gray-100 rounded-xl p-4 lg:p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-100 rounded-full flex items-center justify-center overflow-hidden">
                          {review.userAvatar ? (
                            <img src={review.userAvatar} alt={review.userName} className="w-full h-full object-cover" />
                          ) : (
                            <span className="font-semibold text-emerald-600">{review.userName?.[0] || 'U'}</span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{review.userName}</p>
                          <p className="text-xs text-gray-500">{dateLabel}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600">{review.text}</p>
                  </div>
                );
              }) : (
                <div className="col-span-2 text-center py-8 text-gray-500">
                  <Star className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p>Nessuna recensione ancora</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'info' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3 lg:text-lg">Sedi disponibili</h4>
                <div className="space-y-2">
                  {companyOffices.map((loc) => (
                    <div 
                      key={loc.id} 
                      className={`flex items-center justify-between p-4 rounded-xl transition-colors cursor-pointer ${loc.id === office.id ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 hover:bg-gray-100'}`}
                      onClick={() => loc.id !== office.id && onNavigate('office', { id: loc.id })}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${loc.id === office.id ? 'bg-emerald-200' : 'bg-emerald-100'}`}>
                          <MapPin className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{loc.name}</p>
                          <p className="text-sm text-gray-500">{loc.address}</p>
                          {loc.id === office.id && <span className="text-xs text-emerald-600 font-medium">Sede attuale</span>}
                        </div>
                      </div>
                      {loc.distance && <span className="text-sm text-gray-500">{loc.distance}</span>}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-3 lg:text-lg">Orari di apertura</h4>
                <div className="space-y-2 bg-gray-50 rounded-xl p-4">
                  {(() => {
                    const dayLabels = { mon: 'Lunedì', tue: 'Martedì', wed: 'Mercoledì', thu: 'Giovedì', fri: 'Venerdì', sat: 'Sabato', sun: 'Domenica' };
                    const hoursData = office.hours || {};
                    return Object.entries(dayLabels).map(([key, label]) => {
                      const dayHours = hoursData[key];
                      let displayHours = 'Chiuso';
                      if (dayHours) {
                        if (typeof dayHours === 'string') {
                          displayHours = dayHours;
                        } else if (dayHours.open && dayHours.close) {
                          displayHours = `${dayHours.open} - ${dayHours.close}`;
                          if (dayHours.afternoon) {
                            displayHours += ` / ${dayHours.afternoon.open} - ${dayHours.afternoon.close}`;
                          }
                          if (dayHours.dinner) {
                            displayHours += ` / ${dayHours.dinner.open} - ${dayHours.dinner.close}`;
                          }
                        }
                      }
                      return (
                        <div key={key} className="flex justify-between text-sm py-1">
                          <span className="text-gray-600">{label}</span>
                          <span className={displayHours === 'Chiuso' ? 'text-red-500 font-medium' : 'text-gray-900'}>{displayHours}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
                
                <h4 className="font-medium text-gray-900 mb-3 mt-6 lg:text-lg">Contatti</h4>
                <div className="space-y-3">
                  <a href={`tel:${office.phone}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl text-emerald-600 hover:bg-emerald-50 transition-colors">
                    <Phone className="w-5 h-5" />
                    {office.phone}
                  </a>
                  {office.website && (
                    <a href={`https://${office.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl text-emerald-600 hover:bg-emerald-50 transition-colors">
                      <Globe className="w-5 h-5" />
                      {office.website}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Action Bar - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 z-30 safe-area-bottom lg:hidden">
        <div className="max-w-screen-2xl mx-auto flex items-center gap-3">
          {officeCartItems.length > 0 ? (
            <>
              <div className="flex-1">
                <p className="text-sm text-gray-500">{officeCartItems.reduce((a, c) => a + c.qty, 0)} prodotti nel carrello</p>
                <p className="font-bold text-gray-900">€{cartTotal.toFixed(2)}</p>
              </div>
              <button 
                onClick={() => onNavigate('cart')}
                className="flex-1 bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
              >
                <ShoppingBag className="w-5 h-5" />
                Vai al carrello
              </button>
            </>
          ) : (
            <button 
              onClick={() => setShowBooking(true)}
              className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              Prenota ora
            </button>
          )}
        </div>
      </div>

      {/* Full Screen Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          {/* Gallery Header */}
          <div className="flex items-center justify-between p-4 text-white">
            <button onClick={() => setShowGallery(false)} className="p-2">
              <X className="w-6 h-6" />
            </button>
            <span className="font-medium">{currentImage + 1} / {galleryImages.length}</span>
            <div className="w-10" /> {/* Spacer */}
          </div>

          {/* Gallery Main Image */}
          <div className="flex-1 flex items-center justify-center relative">
            <img 
              src={galleryImages[currentImage]} 
              alt={`${office.name} - Foto ${currentImage + 1}`}
              className="max-w-full max-h-full object-contain"
            />
            
            {/* Navigation */}
            <button 
              onClick={prevImage}
              className="absolute left-4 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            <button 
              onClick={nextImage}
              className="absolute right-4 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          </div>

          {/* Gallery Thumbnails */}
          <div className="p-4">
            <div className="flex gap-2 overflow-x-auto justify-center">
              {galleryImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentImage(i)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    currentImage === i ? 'border-white' : 'border-transparent opacity-50'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBooking && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5 animate-slide-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Prenota {selectedService?.name || 'un servizio'}</h3>
              <button onClick={() => setShowBooking(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {selectedService && (
              <div className="bg-gray-50 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{selectedService.name}</p>
                    <p className="text-sm text-gray-500">{selectedService.duration}</p>
                  </div>
                  <p className="font-bold text-emerald-600">€{selectedService.price}</p>
                </div>
              </div>
            )}

            {/* Office Location */}
            <div className="bg-emerald-50 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-emerald-700">{office.name} - {office.address}</span>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-2">Seleziona una data</p>
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {dates.map((day, i) => (
                  <button 
                    key={i}
                    onClick={() => setSelectedDate(i)}
                    className={`px-4 py-2 rounded-xl whitespace-nowrap transition-all ${selectedDate === i ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-2">Seleziona un orario</p>
              <div className="grid grid-cols-4 gap-2">
                {times.map((time, i) => (
                  <button 
                    key={i}
                    onClick={() => setSelectedTime(i)}
                    className={`py-2 rounded-xl text-sm transition-all ${selectedTime === i ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>

            <button 
              onClick={confirmBooking}
              disabled={selectedTime === null}
              className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="w-5 h-5" />
              Conferma prenotazione
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfficePage;
